<template>
  <div class="customer-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>客户管理</span>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="所属业务员">
          <el-select
            ref="salesmanSelectRef"
            v-model="queryParams.salesmanIds"
            multiple
            filterable
            clearable
            collapse-tags
            collapse-tags-tooltip
            placeholder="请选择业务员"
            style="width: 180px"
            @change="handleSalesmanChange"
            @visible-change="handleSalesmanSelectVisible"
          >
            <el-option
              v-for="salesman in salesmanOptions"
              :key="salesman.id"
              :label="salesman.name"
              :value="salesman.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="客户姓名">
          <el-input
            v-model="queryParams.customerName"
            placeholder="请输入客户姓名"
            clearable
            @keyup.enter="handleSearch"
            style="width: 150px"
          />
        </el-form-item>
        <el-form-item label="客户意向等级">
          <el-select
            v-model="queryParams.intentionLevel"
            placeholder="全部"
            clearable
            style="width: 150px"
          >
            <el-option
              v-for="item in intentionLevelOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="enterpriseCrmEnabled" label="商机阶段">
          <el-select v-model="queryParams.opportunityStage" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in crmStageOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="enterpriseCrmEnabled" label="生命周期">
          <el-select v-model="queryParams.lifecycleStatus" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in crmLifecycleOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="重点跟进">
          <el-select
            v-model="queryParams.followUpReminder"
            placeholder="全部"
            clearable
            style="width: 150px"
          >
            <el-option label="是" :value="1" />
            <el-option label="否" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="创建时间" class="create-time-with-buttons">
          <el-date-picker
            v-model="queryParams.createTimeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 240px"
          />
          <div class="action-buttons-inline">
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
            <el-button type="success" @click="handleExport" :loading="exportLoading">
              <el-icon><Download /></el-icon>
              导出客户
            </el-button>
            <el-button type="warning" plain @click="openPublicSeaDialog">
              公海客户
            </el-button>
          </div>
        </el-form-item>
      </el-form>

      <!-- 客户列表 -->
      <el-table
        v-loading="loading"
        :data="customerList"
        border
        style="width: 100%"
        stripe
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="customerName" label="客户姓名" width="120" show-overflow-tooltip />
        <el-table-column prop="customerPosition" label="客户职位" width="120" show-overflow-tooltip />
        <el-table-column prop="contactPhone" label="客户手机" width="130" />
        <el-table-column prop="followUpReminder" label="重点跟进" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.followUpReminder === 1 ? 'success' : 'info'" size="small">
              {{ row.followUpReminder === 1 ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="intentionLevel" label="意向等级" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getIntentionLevelType(row.intentionLevel)" size="small">
              {{ getIntentionLevelName(row.intentionLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="enterpriseCrmEnabled" label="商机阶段" width="110" show-overflow-tooltip>
          <template #default="{ row }">
            {{ (crmMeta.opportunityStages && row.opportunityStage && crmMeta.opportunityStages[row.opportunityStage]) || row.opportunityStage || '-' }}
          </template>
        </el-table-column>
        <el-table-column v-if="enterpriseCrmEnabled" label="生命周期" width="110" show-overflow-tooltip>
          <template #default="{ row }">
            {{ (crmMeta.lifecycleStatuses && row.lifecycleStatus && crmMeta.lifecycleStatuses[row.lifecycleStatus]) || row.lifecycleStatus || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="address" label="客户地址" width="200" show-overflow-tooltip />
        <el-table-column prop="companyName" label="所属公司" width="150" show-overflow-tooltip />
        <el-table-column prop="salesmanName" label="所属业务员" width="120" show-overflow-tooltip />
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="268" width="288" fixed="right" align="left">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button type="success" size="small" @click="handleViewFollow(row)">跟进记录</el-button>
            <el-button type="info" size="small" plain @click="handleOpenCustomerLog(row)">客户日志</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 客户详情对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="客户详情"
      width="900px"
      @close="handleDialogClose"
    >
      <div v-loading="detailLoading" class="customer-detail">
        <!-- 基础信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">基础信息</span>
              <span class="title-tip">客户基础资料</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="客户姓名">
              {{ customerDetail.customerName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="客户职位">
              {{ customerDetail.customerPosition || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="客户手机">
              {{ customerDetail.contactPhone || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="重点跟进">
              <el-tag :type="customerDetail.followUpReminder === 1 ? 'success' : 'info'" size="small">
                {{ customerDetail.followUpReminder === 1 ? '是' : '否' }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 归属与联系卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">归属与联系</span>
              <span class="title-tip">帮助团队快速定位客户</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="所在地区">
              {{ customerDetail.regionName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="客户地址">
              {{ customerDetail.address || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="所属公司">
              {{ customerDetail.companyName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="所属分组">
              {{ customerDetail.groupName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="关联客户">
              {{ customerDetail.customerSource || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="关联客户手机">
              {{ customerDetail.customerSourcePhone || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 标签与备注卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">标签与备注</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="客户类型">
              <el-tag :type="customerDetail.customerType === 'intention' ? 'warning' : 'info'" size="small">
                {{ customerDetail.customerType === 'intention' ? '意向客户' : (customerDetail.customerType === 'normal' ? '普通客户' : (customerDetail.customerType || '-')) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="意向等级">
              <el-tag :type="getIntentionLevelType(customerDetail.intentionLevel)" size="small">
                {{ getIntentionLevelName(customerDetail.intentionLevel) || '暂无' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="客户信息" :span="2">
              {{ customerDetail.detailInfo || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">
              {{ customerDetail.remark || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="enterpriseCrmEnabled" label="商机阶段">
              {{ (crmMeta.opportunityStages && customerDetail.opportunityStage && crmMeta.opportunityStages[customerDetail.opportunityStage]) || customerDetail.opportunityStage || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="enterpriseCrmEnabled && customerDetail.opportunityStage === crmMeta.opportunityStageLoseCode" label="输单原因">
              {{ (crmMeta.loseReasons && customerDetail.loseReason && crmMeta.loseReasons[customerDetail.loseReason]) || customerDetail.loseReason || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="enterpriseCrmEnabled" label="生命周期">
              {{ (crmMeta.lifecycleStatuses && customerDetail.lifecycleStatus && crmMeta.lifecycleStatuses[customerDetail.lifecycleStatus]) || customerDetail.lifecycleStatus || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="enterpriseCrmEnabled" label="合同到期日">
              {{ customerDetail.contractExpireDate || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="enterpriseCrmEnabled" label="阶段变更记录跟进">
              <el-tag :type="customerDetail.addFollowOnOpportunityStageChange ? 'success' : 'info'" size="small">
                {{ customerDetail.addFollowOnOpportunityStageChange ? '是' : '否' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">
              {{ formatDateTime(customerDetail.createTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="更新时间">
              {{ formatDateTime(customerDetail.updateTime) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </div>
      <template #footer>
        <el-button @click="dialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 跟进记录对话框 -->
    <el-dialog
      v-model="followDialogVisible"
      title="客户跟进记录"
      width="900px"
      @close="handleFollowDialogClose"
    >
      <div v-loading="followLoading" class="follow-record-container">
        <!-- 筛选表单 -->
        <el-form :inline="true" :model="followQueryParams" class="follow-search-form">
          <el-form-item label="跟进日期">
            <el-date-picker
              v-model="followQueryParams.activityTimeRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 240px"
            />
          </el-form-item>
          <el-form-item label="跟进内容">
            <el-input
              v-model="followQueryParams.activityContent"
              placeholder="请输入跟进内容关键词"
              clearable
              style="width: 200px"
              @keyup.enter="handleFollowSearch"
            />
          </el-form-item>
          <el-form-item label="跟进类型">
            <el-select
              v-model="followQueryParams.activityType"
              placeholder="全部"
              clearable
              style="width: 150px"
            >
              <el-option label="拜访" value="visit" />
              <el-option label="电话" value="phone" />
              <el-option label="微信" value="wechat" />
              <el-option label="邮件" value="email" />
              <el-option label="其他" value="other" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleFollowSearch">查询</el-button>
            <el-button @click="handleFollowReset">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 跟进记录列表 -->
        <el-table
          :data="followList"
          border
          stripe
          style="width: 100%"
          :max-height="400"
          :key="`follow-table-${currentCustomerId}`"
        >
          <el-table-column prop="activityType" label="跟进类型" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="getFollowTypeTagType(row.activityType)" size="small">
                {{ getFollowTypeName(row.activityType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="salesmanName" label="跟进人" width="120" show-overflow-tooltip>
            <template #default="{ row }">
              {{ row.salesmanName || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="activityContent" label="跟进内容" show-overflow-tooltip />
          <el-table-column prop="followAddress" label="跟进地址" width="150" show-overflow-tooltip />
          <el-table-column prop="activityTime" label="跟进日期" width="180" fixed="right">
            <template #default="{ row }">
              {{ formatDateTime(row.activityTime) }}
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="follow-pagination">
          <el-pagination
            v-model:current-page="followQueryParams.page"
            v-model:page-size="followQueryParams.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="followTotal"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleFollowSizeChange"
            @current-change="handleFollowPageChange"
          />
        </div>
      </div>
      <template #footer>
        <el-button @click="followDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 客户操作日志 -->
    <!-- 公海客户 -->
    <el-dialog
      v-model="publicSeaDialogVisible"
      title="公海客户"
      width="1180px"
      top="4vh"
      destroy-on-close
      class="public-sea-dialog"
      @open="handlePublicSeaDialogOpen"
    >
      <el-form :inline="true" :model="publicSeaQuery" class="search-form">
        <el-form-item label="客户姓名">
          <el-input
            v-model="publicSeaQuery.customerName"
            placeholder="姓名或手机"
            clearable
            style="width: 150px"
            @keyup.enter="handlePublicSeaSearch"
          />
        </el-form-item>
        <el-form-item label="客户手机">
          <el-input
            v-model="publicSeaQuery.contactPhone"
            placeholder="手机号"
            clearable
            style="width: 140px"
            @keyup.enter="handlePublicSeaSearch"
          />
        </el-form-item>
        <el-form-item label="意向等级">
          <el-select v-model="publicSeaQuery.intentionLevel" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in intentionLevelOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="enterpriseCrmEnabled" label="商机阶段">
          <el-select v-model="publicSeaQuery.opportunityStage" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in crmStageOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="enterpriseCrmEnabled" label="生命周期">
          <el-select v-model="publicSeaQuery.lifecycleStatus" placeholder="全部" clearable style="width: 150px">
            <el-option
              v-for="item in crmLifecycleOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="重点跟进">
          <el-select v-model="publicSeaQuery.followUpReminder" placeholder="全部" clearable style="width: 120px">
            <el-option label="是" :value="1" />
            <el-option label="否" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="创建时间">
          <el-date-picker
            v-model="publicSeaQuery.createTimeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 240px"
          />
        </el-form-item>
        <el-form-item class="public-sea-form-actions">
          <div class="public-sea-action-btns">
            <el-button type="primary" @click="handlePublicSeaSearch">查询</el-button>
            <el-button @click="handlePublicSeaReset">重置</el-button>
            <el-button type="primary" plain :loading="publicSeaTemplateLoading" @click="handlePublicSeaDownloadTemplate">
              <el-icon><Download /></el-icon>
              下载导入模板
            </el-button>
            <el-popover
              placement="bottom"
              trigger="click"
              width="360"
              popper-class="public-sea-import-popper"
            >
              <template #reference>
                <el-button type="success" plain :loading="publicSeaImportLoading">
                  导入
                </el-button>
              </template>
              <div class="import-panel">
                <div class="import-panel-title">拖拽 Excel 到下方区域，或点击选择文件导入</div>
                <el-upload
                  drag
                  action="#"
                  :show-file-list="false"
                  :auto-upload="false"
                  accept=".xlsx,.xls"
                  :before-upload="beforePublicSeaImportUpload"
                  :on-change="handlePublicSeaUploadChange"
                >
                  <el-icon class="el-icon--upload"><Download /></el-icon>
                  <div class="el-upload__text">
                    将文件拖到此处，或<em>点击上传</em>
                  </div>
                  <template #tip>
                    <div class="el-upload__tip">仅支持 .xlsx / .xls，单次 1 个文件</div>
                  </template>
                </el-upload>
              </div>
            </el-popover>
            <el-button type="success" :loading="publicSeaExportLoading" @click="handlePublicSeaExport">
              <el-icon><Download /></el-icon>
              导出公海
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <el-table
        v-loading="publicSeaLoading"
        :data="publicSeaList"
        border
        stripe
        max-height="420"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="72" />
        <el-table-column prop="customerName" label="客户姓名" width="110" show-overflow-tooltip />
        <el-table-column prop="customerPosition" label="客户职位" width="100" show-overflow-tooltip />
        <el-table-column prop="contactPhone" label="客户手机" width="118" />
        <el-table-column label="状态" width="88" align="center">
          <template #default="{ row }">
            <el-tag :type="row.deleted === 1 ? 'danger' : 'success'" size="small">
              {{ row.deleted === 1 ? '已禁用' : '正常' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="followUpReminder" label="重点跟进" width="92" align="center">
          <template #default="{ row }">
            <el-tag :type="row.followUpReminder === 1 ? 'success' : 'info'" size="small">
              {{ row.followUpReminder === 1 ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="intentionLevel" label="意向等级" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getIntentionLevelType(row.intentionLevel)" size="small">
              {{ getIntentionLevelName(row.intentionLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="enterpriseCrmEnabled" label="商机阶段" width="100" show-overflow-tooltip>
          <template #default="{ row }">
            {{ (crmMeta.opportunityStages && row.opportunityStage && crmMeta.opportunityStages[row.opportunityStage]) || row.opportunityStage || '-' }}
          </template>
        </el-table-column>
        <el-table-column v-if="enterpriseCrmEnabled" label="生命周期" width="100" show-overflow-tooltip>
          <template #default="{ row }">
            {{ (crmMeta.lifecycleStatuses && row.lifecycleStatus && crmMeta.lifecycleStatuses[row.lifecycleStatus]) || row.lifecycleStatus || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="address" label="客户地址" min-width="140" show-overflow-tooltip />
        <el-table-column prop="companyName" label="所属公司" width="120" show-overflow-tooltip />
        <el-table-column prop="createTime" label="创建时间" width="108">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="290" fixed="right" align="left">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button type="info" size="small" plain @click="handleOpenCustomerLog(row)">客户日志</el-button>
            <el-button
              v-if="row.deleted !== 1"
              type="warning"
              size="small"
              @click="handlePublicSeaToggleEnabled(row, false)"
            >禁用</el-button>
            <el-button
              v-else
              type="success"
              size="small"
              @click="handlePublicSeaToggleEnabled(row, true)"
            >启用</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination" style="margin-top: 12px">
        <el-pagination
          v-model:current-page="publicSeaQuery.page"
          v-model:page-size="publicSeaQuery.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="publicSeaTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadPublicSeaList"
          @current-change="loadPublicSeaList"
        />
      </div>
      <template #footer>
        <el-button type="primary" @click="publicSeaDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="customerLogDialogVisible"
      :title="customerLogDialogTitle"
      width="920px"
      class="customer-log-dialog"
      destroy-on-close
      @closed="handleCustomerLogDialogClosed"
    >
      <div v-loading="customerLogLoading" class="customer-log-list-wrap">
        <div v-if="!customerLogDisplayList.length" class="customer-log-empty">暂无操作记录</div>
        <div v-else class="customer-log-list">
          <div v-for="item in customerLogDisplayList" :key="item.id" class="customer-log-card">
            <div
              v-for="(line, index) in item.lines"
              :key="`${item.id}-${index}`"
              :class="['customer-log-line', index === 0 ? 'is-main' : 'is-sub']"
            >
              {{ line }}
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" @click="customerLogDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, onActivated } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Download } from '@element-plus/icons-vue'
import {
  getCustomerList,
  getCustomerDetail,
  getIntentionLevels,
  getCustomerFollowList,
  getManagerCustomerOperationLogs,
  exportCustomerList,
  exportPublicSeaCustomerList,
  getManagerCustomerCrmMeta,
  getPublicSeaCustomerList,
  setPublicSeaCustomerEnabled,
  downloadPublicSeaImportTemplate,
  importPublicSeaCustomerList
} from '../api/managerCustomer'
import { getSalesmanList } from '../api/managerUser'
import { setupPageCacheRefresh, setPageRefreshTime, shouldRefreshPage } from '../utils/pageCache'

const route = useRoute()
const router = useRouter()

const crmMeta = ref({
  enterpriseCrmEnabled: false,
  opportunityStages: {},
  lifecycleStatuses: {},
  loseReasons: {},
  opportunityStageLoseCode: ''
})
const enterpriseCrmEnabled = computed(() => !!crmMeta.value?.enterpriseCrmEnabled)
const crmStageOptions = computed(() => {
  const m = crmMeta.value?.opportunityStages || {}
  return Object.keys(m).map((code) => ({ value: code, label: m[code] || code }))
})
const crmLifecycleOptions = computed(() => {
  const m = crmMeta.value?.lifecycleStatuses || {}
  return Object.keys(m).map((code) => ({ value: code, label: m[code] || code }))
})
const loadCrmMeta = async () => {
  try {
    const res = await getManagerCustomerCrmMeta()
    if (res.code === '000000' && res.data) {
      crmMeta.value = { ...crmMeta.value, ...res.data }
    }
  } catch (e) {
    console.warn('加载 CRM 元数据失败', e)
  }
}

// 数据
const loading = ref(false)
const detailLoading = ref(false)
const exportLoading = ref(false)
const publicSeaDialogVisible = ref(false)
const publicSeaLoading = ref(false)
const publicSeaExportLoading = ref(false)
const publicSeaTemplateLoading = ref(false)
const publicSeaImportLoading = ref(false)
const publicSeaList = ref([])
const publicSeaTotal = ref(0)
const publicSeaQuery = reactive({
  customerName: '',
  contactPhone: '',
  intentionLevel: '',
  followUpReminder: null,
  opportunityStage: '',
  lifecycleStatus: '',
  createTimeRange: null,
  page: 1,
  pageSize: 10
})
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const customerList = ref([])
const total = ref(0)
const salesmanOptions = ref([])
const intentionLevelOptions = ref([])
const salesmanSelectRef = ref(null)
const keepSalesmanSelectOpen = ref(false)

const queryParams = reactive({
  customerName: '',
  salesmanIds: [],
  intentionLevel: '',
  followUpReminder: null,
  opportunityStage: '',
  lifecycleStatus: '',
  page: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const customerDetail = reactive({
  id: null,
  customerName: '',
  customerPosition: '',
  contactPhone: '',
  followUpReminder: 0,
  regionName: '',
  address: '',
  companyName: '',
  groupName: '',
  customerSource: '',
  customerSourcePhone: '',
  customerType: '',
  intentionLevel: '',
  detailInfo: '',
  remark: '',
  createTime: null,
  updateTime: null,
  opportunityStage: '',
  loseReason: '',
  lifecycleStatus: '',
  contractExpireDate: ''
})

// 跟进记录对话框
const followDialogVisible = ref(false)
const followLoading = ref(false)
const followList = ref([])
const followTotal = ref(0)
const currentCustomerId = ref(null)
const followQueryParams = reactive({
  activityTimeRange: null,
  activityContent: '',
  activityType: '',
  page: 1,
  pageSize: 10
})

/** 客户操作日志（公海/归属/资料变更等） */
const customerLogDialogVisible = ref(false)
const customerLogDialogTitle = ref('客户日志')
const customerLogLoading = ref(false)
const customerLogList = ref([])

// 方法
const getIntentionLevelName = (level) => {
  if (!level) return '暂无'
  // 支持大小写匹配
  const option = intentionLevelOptions.value.find(item => 
    item.value === level || 
    item.value?.toUpperCase() === level?.toUpperCase() ||
    item.value?.toLowerCase() === level?.toLowerCase()
  )
  if (option) {
    return option.label
  }
  // 如果找不到匹配项，使用默认的中文映射
  const defaultMap = {
    'INTENTION': '意向',
    'HIGH': '高',
    'MEDIUM': '中',
    'LOW': '低',
    '高': '高',
    '中': '中',
    '低': '低'
  }
  // 支持大小写不敏感的匹配
  const upperLevel = level.toUpperCase()
  return defaultMap[upperLevel] || defaultMap[level] || level
}

const getIntentionLevelType = (level) => {
  if (!level) return 'info'
  const map = {
    'HIGH': 'danger',
    'MEDIUM': 'warning',
    'LOW': 'info'
  }
  return map[level] || 'info'
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  if (Number.isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatLogDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  if (Number.isNaN(date.getTime())) return '-'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  const second = String(date.getSeconds()).padStart(2, '0')
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`
}

const getCustomerLogOperatorLabel = (row) => {
  const operatorId = row?.operatorUserId
  const name = row?.operatorName
  if (operatorId === 0 || operatorId === '0') return '系统'
  if (name && String(name).trim()) return String(name).trim()
  if (operatorId != null && operatorId !== '') return `用户#${operatorId}`
  return '系统'
}

const getCustomerLogSalesmanLabel = (id, name) => {
  if (name && String(name).trim()) return String(name).trim()
  if (id != null && id !== '') return `业务员#${id}`
  return '公海'
}

const buildCustomerLogLines = (row) => {
  const timeText = formatLogDateTime(row?.createTime)
  const who = getCustomerLogOperatorLabel(row)
  const customerName = row?.customerName || row?.contactName || row?.customerDisplayName || '该客户'
  const partySalesman = row?.partySalesmanName || (row?.partySalesmanId != null ? `业务员#${row.partySalesmanId}` : '')
  const ownerBefore = getCustomerLogSalesmanLabel(row?.ownerBeforeSalesmanId, row?.ownerBeforeSalesmanName)
  const ownerAfter = getCustomerLogSalesmanLabel(row?.ownerAfterSalesmanId, row?.ownerAfterSalesmanName)
  const remark = (row?.remark || '').trim()
  const opType = row?.opType || ''
  const lines = []

  const pushRemarkIfNeeded = () => {
    if (!remark || opType === 'INFO_UPDATE') return
    lines.push(remark)
  }

  switch (opType) {
    case 'CLAIM_FROM_POOL':
      lines.push(`${timeText}，${who}认领了「${customerName}」。`)
      if (partySalesman) lines.push(`该客户之前归属于「${partySalesman}」。`)
      pushRemarkIfNeeded()
      break
    case 'MANUAL_RELEASE_POOL':
      lines.push(`${timeText}，${who}把「${customerName}」退回了公海。`)
      pushRemarkIfNeeded()
      break
    case 'AUTO_RECYCLE_POOL':
      lines.push(`${timeText}，${who}将「${customerName}」自动退回了公海。`)
      pushRemarkIfNeeded()
      break
    case 'INFO_UPDATE':
      lines.push(`${timeText}，${who}修改了客户「${customerName}」的资料。`)
      if (remark) lines.push(remark)
      break
    case 'CUSTOMER_CREATE':
      lines.push(`${timeText}，${who}新建了客户「${customerName}」。`)
      pushRemarkIfNeeded()
      break
    case 'ADMIN_ASSIGN_POOL':
      lines.push(`${timeText}，${who}将客户「${customerName}」分配给了「${ownerAfter}」。`)
      pushRemarkIfNeeded()
      break
    case 'OWNER_CHANGE':
      lines.push(`${timeText}，「${customerName}」的归属由「${ownerBefore}」变为「${ownerAfter}」。`)
      pushRemarkIfNeeded()
      break
    default:
      lines.push(`${timeText}，${who}对「${customerName}」进行了「${row?.opTypeLabel || opType || '操作'}」。`)
      if (ownerBefore !== '公海' || ownerAfter !== '公海') {
        lines.push(`归属由「${ownerBefore}」变为「${ownerAfter}」。`)
      } else if (partySalesman) {
        lines.push(`关联业务员：「${partySalesman}」。`)
      }
      pushRemarkIfNeeded()
      break
  }

  return lines.length ? lines : [`${timeText}，${who}操作了「${customerName}」。`]
}

const customerLogDisplayList = computed(() =>
  customerLogList.value.map((row) => ({
    id: row.id,
    lines: buildCustomerLogLines(row)
  }))
)

const loadCustomerOperationLogs = async (customerId) => {
  customerLogLoading.value = true
  customerLogList.value = []
  try {
    const res = await getManagerCustomerOperationLogs(customerId)
    if (res.code === '000000') {
      customerLogList.value = Array.isArray(res.data) ? res.data : []
    } else {
      ElMessage.error(res.message || res.msg || '加载客户日志失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('加载客户日志失败')
  } finally {
    customerLogLoading.value = false
  }
}

const handleOpenCustomerLog = async (row) => {
  if (!row?.id) return
  const name = row.customerName || row.contactName || ''
  customerLogDialogTitle.value = name ? `客户日志 — ${name}（ID ${row.id}）` : `客户日志（ID ${row.id}）`
  customerLogDialogVisible.value = true
  await loadCustomerOperationLogs(row.id)
}

const handleCustomerLogDialogClosed = () => {
  customerLogList.value = []
}

// 加载业务员列表
const loadSalesmanList = async () => {
  try {
    const res = await getSalesmanList()
    if (res.code === '000000') {
      salesmanOptions.value = res.data || []
    }
  } catch (error) {
    console.error('获取业务员列表失败:', error)
  }
}

// 加载意向等级字典
const loadIntentionLevelOptions = async () => {
  try {
    const res = await getIntentionLevels()
    if (res.code === '000000') {
      // 将字典数据转换为选项格式，使用dataCode作为value，dataName作为label
      intentionLevelOptions.value = (res.data || []).map(item => ({
        label: item.dataName,
        value: item.dataCode
      }))
    } else {
      ElMessage.warning('获取意向等级字典失败，使用默认值')
      // 如果获取失败，使用默认值作为兜底
      intentionLevelOptions.value = [
        { label: '高', value: 'HIGH' },
        { label: '中', value: 'MEDIUM' },
        { label: '低', value: 'LOW' }
      ]
    }
  } catch (error) {
    console.error('获取意向等级字典失败:', error)
    ElMessage.warning('获取意向等级字典失败，使用默认值')
    // 如果获取失败，使用默认值作为兜底
    intentionLevelOptions.value = [
      { label: '高', value: 'HIGH' },
      { label: '中', value: 'MEDIUM' },
      { label: '低', value: 'LOW' }
    ]
  }
}

const loadCustomerList = async () => {
  loading.value = true
  try {
    // 记录刷新时间
    setPageRefreshTime(PAGE_CACHE_KEY)
    const params = {
      page: queryParams.page,
      pageSize: queryParams.pageSize
    }
    if (queryParams.customerName) {
      params.customerName = queryParams.customerName
    }
    if (queryParams.salesmanIds && queryParams.salesmanIds.length > 0) {
      params.salesmanIds = queryParams.salesmanIds.join(',')
    }
    if (queryParams.intentionLevel) {
      params.intentionLevel = queryParams.intentionLevel
    }
    if (queryParams.followUpReminder !== null && queryParams.followUpReminder !== undefined) {
      params.followUpReminder = queryParams.followUpReminder
    }
    if (queryParams.createTimeRange && queryParams.createTimeRange.length === 2) {
      params.createTimeStart = queryParams.createTimeRange[0]
      params.createTimeEnd = queryParams.createTimeRange[1]
    }
    if (enterpriseCrmEnabled.value && queryParams.opportunityStage) {
      params.opportunityStage = queryParams.opportunityStage
    }
    if (enterpriseCrmEnabled.value && queryParams.lifecycleStatus) {
      params.lifecycleStatus = queryParams.lifecycleStatus
    }
    const res = await getCustomerList(params)
    if (res.code === '000000') {
      customerList.value = res.data?.list || []
      total.value = res.data?.total || 0
    } else {
      ElMessage.error(res.message || '获取客户列表失败')
    }
  } catch (error) {
    console.error('获取客户列表失败:', error)
    ElMessage.error('获取客户列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.page = 1
  loadCustomerList()
}

const handleReset = () => {
  queryParams.customerName = ''
  queryParams.salesmanIds = []
  queryParams.intentionLevel = ''
  queryParams.followUpReminder = null
  queryParams.opportunityStage = ''
  queryParams.lifecycleStatus = ''
  queryParams.createTimeRange = null
  queryParams.page = 1
  loadCustomerList()
}

// 处理业务员选择变化
const handleSalesmanChange = () => {
  // 当选择发生变化时，标记需要保持下拉框打开
  keepSalesmanSelectOpen.value = true
  // 延迟重新打开下拉框，确保选择操作完成
  nextTick(() => {
    setTimeout(() => {
      if (keepSalesmanSelectOpen.value && salesmanSelectRef.value) {
        // 重新打开下拉框
        const selectEl = salesmanSelectRef.value.$el
        if (selectEl) {
          // 查找输入框并点击以打开下拉框
          const input = selectEl.querySelector('input')
          if (input) {
            // 先聚焦，再点击，确保下拉框打开
            input.focus()
            setTimeout(() => {
              input.click()
            }, 10)
          }
        }
        keepSalesmanSelectOpen.value = false
      }
    }, 100)
  })
}

// 处理业务员下拉框显示/隐藏
const handleSalesmanSelectVisible = (visible) => {
  // 如果下拉框关闭了，但我们需要保持打开，则重新打开
  if (!visible && keepSalesmanSelectOpen.value) {
    keepSalesmanSelectOpen.value = false
    nextTick(() => {
      setTimeout(() => {
        if (salesmanSelectRef.value) {
          const selectEl = salesmanSelectRef.value.$el
          if (selectEl) {
            const input = selectEl.querySelector('input')
            if (input) {
              input.focus()
              setTimeout(() => {
                input.click()
              }, 10)
            }
          }
        }
      }, 50)
    })
  }
}

// 导出客户列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }
  
  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    // 构建导出参数（复用查询条件，但不包含分页参数）
    const params = {}
    if (queryParams.customerName) {
      params.customerName = queryParams.customerName
    }
    if (queryParams.salesmanIds && queryParams.salesmanIds.length > 0) {
      params.salesmanIds = queryParams.salesmanIds.join(',')
    }
    if (queryParams.intentionLevel) {
      params.intentionLevel = queryParams.intentionLevel
    }
    if (queryParams.followUpReminder !== null && queryParams.followUpReminder !== undefined) {
      params.followUpReminder = queryParams.followUpReminder
    }
    if (queryParams.createTimeRange && queryParams.createTimeRange.length === 2) {
      params.createTimeStart = queryParams.createTimeRange[0]
      params.createTimeEnd = queryParams.createTimeRange[1]
    }
    if (enterpriseCrmEnabled.value && queryParams.opportunityStage) {
      params.opportunityStage = queryParams.opportunityStage
    }
    if (enterpriseCrmEnabled.value && queryParams.lifecycleStatus) {
      params.lifecycleStatus = queryParams.lifecycleStatus
    }
    
    const blob = await exportCustomerList(params)
    
    // 检查返回的是否是Blob对象
    if (!(blob instanceof Blob)) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // 生成文件名
    const fileName = `客户列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
    link.download = fileName
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出客户列表失败:', error)
    const errorMsg = error.message || '未知错误'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error('导出失败：' + errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

const openPublicSeaDialog = () => {
  publicSeaDialogVisible.value = true
}

const handlePublicSeaDialogOpen = () => {
  publicSeaQuery.page = 1
  loadPublicSeaList()
}

const buildPublicSeaListParams = () => {
  const params = {
    page: publicSeaQuery.page,
    pageSize: publicSeaQuery.pageSize
  }
  if (publicSeaQuery.customerName) params.customerName = publicSeaQuery.customerName
  if (publicSeaQuery.contactPhone) params.contactPhone = publicSeaQuery.contactPhone
  if (publicSeaQuery.intentionLevel) params.intentionLevel = publicSeaQuery.intentionLevel
  if (publicSeaQuery.followUpReminder !== null && publicSeaQuery.followUpReminder !== undefined) {
    params.followUpReminder = publicSeaQuery.followUpReminder
  }
  if (publicSeaQuery.createTimeRange && publicSeaQuery.createTimeRange.length === 2) {
    params.createTimeStart = publicSeaQuery.createTimeRange[0]
    params.createTimeEnd = publicSeaQuery.createTimeRange[1]
  }
  if (enterpriseCrmEnabled.value && publicSeaQuery.opportunityStage) {
    params.opportunityStage = publicSeaQuery.opportunityStage
  }
  if (enterpriseCrmEnabled.value && publicSeaQuery.lifecycleStatus) {
    params.lifecycleStatus = publicSeaQuery.lifecycleStatus
  }
  return params
}

const loadPublicSeaList = async () => {
  publicSeaLoading.value = true
  try {
    const res = await getPublicSeaCustomerList(buildPublicSeaListParams())
    if (res.code === '000000') {
      publicSeaList.value = res.data?.list || []
      publicSeaTotal.value = res.data?.total || 0
    } else {
      ElMessage.error(res.message || res.msg || '加载公海客户失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('加载公海客户失败')
  } finally {
    publicSeaLoading.value = false
  }
}

const handlePublicSeaSearch = () => {
  publicSeaQuery.page = 1
  loadPublicSeaList()
}

const handlePublicSeaReset = () => {
  publicSeaQuery.customerName = ''
  publicSeaQuery.contactPhone = ''
  publicSeaQuery.intentionLevel = ''
  publicSeaQuery.followUpReminder = null
  publicSeaQuery.opportunityStage = ''
  publicSeaQuery.lifecycleStatus = ''
  publicSeaQuery.createTimeRange = null
  publicSeaQuery.page = 1
  loadPublicSeaList()
}

const handlePublicSeaDownloadTemplate = async () => {
  publicSeaTemplateLoading.value = true
  try {
    const blob = await downloadPublicSeaImportTemplate()
    if (!(blob instanceof Blob)) throw new Error('下载失败')
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '公海客户导入模板.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    ElMessage.success('模板已下载')
  } catch (e) {
    console.error(e)
    ElMessage.error(e.message || '下载模板失败')
  } finally {
    publicSeaTemplateLoading.value = false
  }
}

const beforePublicSeaImportUpload = (file) => {
  const fileName = file?.name || ''
  const isExcel = /\.(xlsx|xls)$/i.test(fileName)
  if (!isExcel) {
    ElMessage.warning('仅支持上传 .xlsx 或 .xls 文件')
    return false
  }
  return true
}

const handlePublicSeaUploadChange = async (uploadFile) => {
  const file = uploadFile?.raw
  if (!file) return
  await importPublicSeaFile(file)
}

const importPublicSeaFile = async (file) => {
  publicSeaImportLoading.value = true
  try {
    const res = await importPublicSeaCustomerList(file)
    const payload = res?.data || res
    if (payload && payload.code === '000000' && payload.data) {
      const d = payload.data || {}
      const success = d.success ?? 0
      const fail = d.fail ?? 0
      const total = d.total ?? (success + fail)

      if (fail > 0) {
        const errors = Array.isArray(d.errorMessages) ? d.errorMessages.filter(Boolean) : []
        const preview = errors.slice(0, 8).join('<br/>')
        const moreTip = errors.length > 8 ? `<br/>... 其余 ${errors.length - 8} 条请下载失败明细查看` : ''
        const content = `
          <div style="line-height:1.7;">
            <div>导入完成：共 ${total} 条，成功 ${success} 条，失败 ${fail} 条。</div>
            ${preview ? `<div style="margin-top:8px;color:#e6a23c;">${preview}${moreTip}</div>` : ''}
          </div>
        `
        try {
          await ElMessageBox.confirm(content, '导入存在失败数据', {
            type: 'warning',
            dangerouslyUseHTMLString: true,
            confirmButtonText: '下载失败明细',
            cancelButtonText: '我知道了'
          })
          if (d.importRecordId) {
            await downloadPublicSeaFailExcelByRecordId(d.importRecordId)
          } else {
            ElMessage.warning('失败明细暂不可下载，请稍后重试')
          }
        } catch {
          // 用户取消下载
        }
      } else {
        ElMessage.success(`导入完成：共 ${total} 条，成功 ${success} 条`)
      }
      loadPublicSeaList()
    } else {
      ElMessage.error(payload?.msg || payload?.message || '导入失败')
    }
  } catch (err) {
    const msg = err?.response?.data?.msg || err?.response?.data?.message || err?.message || '导入失败'
    ElMessage.error(msg)
  } finally {
    publicSeaImportLoading.value = false
  }
}

const downloadPublicSeaFailExcelByRecordId = async (importRecordId) => {
  try {
    const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
    const token = localStorage.getItem('accessToken')
    const response = await fetch(
      `${baseURL}/manager/customer/public-sea/import-fail-excel/${importRecordId}`,
      {
        method: 'GET',
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      }
    )
    if (!response.ok) throw new Error('下载失败')
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `公海导入失败记录_${importRecordId}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
    ElMessage.success('下载成功')
  } catch (e) {
    console.error(e)
    ElMessage.error('下载失败')
  }
}

const handlePublicSeaExport = async () => {
  if (publicSeaExportLoading.value) return
  publicSeaExportLoading.value = true
  try {
    const params = buildPublicSeaListParams()
    delete params.page
    delete params.pageSize
    const blob = await exportPublicSeaCustomerList(params)
    if (!(blob instanceof Blob)) throw new Error('导出失败')
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `公海客户_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (e) {
    console.error(e)
    ElMessage.error(e.message || '导出失败')
  } finally {
    publicSeaExportLoading.value = false
  }
}

const handlePublicSeaToggleEnabled = async (row, enabled) => {
  const tip = enabled
    ? `确定启用客户「${row.customerName || ''}」吗？`
    : `确定禁用客户「${row.customerName || ''}」吗？仅在库中记录禁用状态，仍保留在公海列表中，可随时启用。`
  try {
    await ElMessageBox.confirm(tip, '提示', { type: enabled ? 'success' : 'warning' })
  } catch {
    return
  }
  try {
    const res = await setPublicSeaCustomerEnabled(row.id, enabled)
    if (res.code === '000000') {
      ElMessage.success(enabled ? '已启用' : '已禁用')
      publicSeaQuery.page = 1
      loadPublicSeaList()
    } else {
      ElMessage.error(res.message || res.msg || '操作失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('操作失败')
  }
}

const handleView = async (row) => {
  detailLoading.value = true
  dialogVisible.value = true
  try {
    const res = await getCustomerDetail(row.id)
    if (res.code === '000000') {
      // 清空旧数据
      Object.keys(customerDetail).forEach(key => {
        customerDetail[key] = null
      })
      // 填充新数据
      Object.assign(customerDetail, res.data)
    } else {
      ElMessage.error(res.message || '获取客户详情失败')
      dialogVisible.value = false
    }
  } catch (error) {
    console.error('获取客户详情失败:', error)
    ElMessage.error('获取客户详情失败')
    dialogVisible.value = false
  } finally {
    detailLoading.value = false
  }
}

const handleDialogClose = () => {
  // 清空详情数据
  Object.keys(customerDetail).forEach(key => {
    customerDetail[key] = null
  })
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadCustomerList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadCustomerList()
}

// 跟进记录相关方法
const handleViewFollow = async (row) => {
  currentCustomerId.value = row.id
  // 重置查询条件
  followQueryParams.activityTimeRange = null
  followQueryParams.activityContent = ''
  followQueryParams.activityType = ''
  followQueryParams.page = 1
  followQueryParams.pageSize = 10
  // 先打开弹窗
  followDialogVisible.value = true
  // 等待DOM渲染完成后再加载数据，避免ResizeObserver警告
  await nextTick()
  // 加载跟进记录
  loadFollowList()
}

const loadFollowList = async () => {
  if (!currentCustomerId.value) {
    return
  }
  followLoading.value = true
  try {
    const params = {
      customerId: currentCustomerId.value,
      page: followQueryParams.page,
      pageSize: followQueryParams.pageSize
    }
    if (followQueryParams.activityTimeRange && followQueryParams.activityTimeRange.length === 2) {
      params.activityTimeStart = followQueryParams.activityTimeRange[0]
      params.activityTimeEnd = followQueryParams.activityTimeRange[1]
    }
    if (followQueryParams.activityContent) {
      params.activityContent = followQueryParams.activityContent.trim()
    }
    if (followQueryParams.activityType) {
      params.activityType = followQueryParams.activityType
    }
    const res = await getCustomerFollowList(params)
    if (res.code === '000000') {
      followList.value = res.data?.list || []
      followTotal.value = res.data?.total || 0
    } else {
      ElMessage.error(res.message || '获取跟进记录失败')
    }
  } catch (error) {
    console.error('获取跟进记录失败:', error)
    ElMessage.error('获取跟进记录失败')
  } finally {
    followLoading.value = false
  }
}

const handleFollowSearch = () => {
  followQueryParams.page = 1
  loadFollowList()
}

const handleFollowReset = () => {
  followQueryParams.activityTimeRange = null
  followQueryParams.activityContent = ''
  followQueryParams.activityType = ''
  followQueryParams.page = 1
  loadFollowList()
}

const handleFollowSizeChange = (val) => {
  followQueryParams.pageSize = val
  followQueryParams.page = 1
  loadFollowList()
}

const handleFollowPageChange = (val) => {
  followQueryParams.page = val
  loadFollowList()
}

const handleFollowDialogClose = () => {
  followList.value = []
  followTotal.value = 0
  currentCustomerId.value = null
}

// 跟进类型相关方法
const getFollowTypeName = (type) => {
  if (!type) return '-'
  const typeMap = {
    'visit': '拜访',
    'VISIT': '拜访',
    'phone': '电话',
    'PHONE': '电话',
    'wechat': '微信',
    'WECHAT': '微信',
    'email': '邮件',
    'EMAIL': '邮件',
    'other': '其他',
    'OTHER': '其他'
  }
  return typeMap[type] || typeMap[type.toUpperCase()] || type
}

const getFollowTypeTagType = (type) => {
  if (!type) return 'info'
  const typeMap = {
    'visit': 'primary',
    'VISIT': 'primary',
    'phone': 'success',
    'PHONE': 'success',
    'wechat': 'warning',
    'WECHAT': 'warning',
    'email': 'info',
    'EMAIL': 'info',
    'other': '',
    'OTHER': ''
  }
  return typeMap[type] || typeMap[type.toUpperCase()] || 'info'
}

// 页面缓存键
const PAGE_CACHE_KEY = 'manager-customer-list'

// 清理函数
let cleanupCacheRefresh = null

onMounted(async () => {
  await loadSalesmanList()
  await loadIntentionLevelOptions()
  await loadCrmMeta()
  
  // 检查是否从首页点击本月新增客户进入（通过 query 参数判断）
  const autoMonthFilter = route.query.autoMonthFilter === 'true'
  const createTimeStart = route.query.createTimeStart
  const createTimeEnd = route.query.createTimeEnd
  
  if (autoMonthFilter && createTimeStart && createTimeEnd) {
    // 从首页点击本月新增客户进入，设置日期范围并自动查询
    console.log('从首页点击本月新增客户进入，设置日期范围:', createTimeStart, '至', createTimeEnd)
    queryParams.createTimeRange = [createTimeStart, createTimeEnd]
    queryParams.page = 1
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  }
  
  loadCustomerList()
  
  // 设置缓存刷新监听
  cleanupCacheRefresh = setupPageCacheRefresh(() => {
    if (!loading.value) {
      loadCustomerList()
    }
  })
})

onUnmounted(() => {
  // 清理缓存刷新监听
  if (cleanupCacheRefresh) {
    cleanupCacheRefresh()
    cleanupCacheRefresh = null
  }
})

// 当页面从缓存中激活时（keep-alive）
onActivated(() => {
  // 检查是否从首页点击本月新增客户进入（通过 query 参数判断）
  const autoMonthFilter = route.query.autoMonthFilter === 'true'
  const createTimeStart = route.query.createTimeStart
  const createTimeEnd = route.query.createTimeEnd
  
  if (autoMonthFilter && createTimeStart && createTimeEnd) {
    // 从首页点击本月新增客户进入，设置日期范围并自动查询（即使页面缓存存在）
    console.log('从首页点击本月新增客户进入（页面激活），设置日期范围:', createTimeStart, '至', createTimeEnd)
    queryParams.createTimeRange = [createTimeStart, createTimeEnd]
    queryParams.page = 1
    loadCustomerList()
    // 清除 query 参数，避免刷新时重复触发
    router.replace({ path: route.path, query: {} })
  } else {
    // 页面激活时，如果数据超过2分钟未刷新，则自动刷新
    if (shouldRefreshPage(PAGE_CACHE_KEY, 120000)) {
      console.log('页面激活：数据已过期，自动刷新')
      loadCustomerList()
    }
  }
})
</script>

<style scoped>
.customer-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.create-time-with-buttons {
  margin-right: 0;
}

.create-time-with-buttons :deep(.el-form-item__content) {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-buttons-inline {
  display: flex;
  gap: 10px;
  margin-left: 0;
  flex-shrink: 0;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.customer-detail {
  padding: 0;
}

.detail-card {
  margin-bottom: 20px;
}

.detail-card:last-child {
  margin-bottom: 0;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.title-tip {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

:deep(.el-descriptions__label) {
  font-weight: 500;
  width: 120px;
}

:deep(.el-descriptions__content) {
  word-break: break-word;
}

/* 跟进记录弹窗样式 */
.follow-record-container {
  padding: 0;
}

.follow-search-form {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.follow-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.customer-log-list-wrap {
  min-height: 180px;
  max-height: 520px;
  overflow-y: auto;
}

.customer-log-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.customer-log-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #f7f9fc;
  padding: 14px 16px;
}

.customer-log-line {
  line-height: 1.75;
  word-break: break-word;
}

.customer-log-line.is-main {
  color: #303133;
  font-size: 15px;
  font-weight: 500;
}

.customer-log-line.is-sub {
  margin-top: 4px;
  color: #8a94a6;
  font-size: 14px;
}

.customer-log-empty {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
}
</style>

<style>
/* el-dialog 默认 teleport 到 body，公海弹窗样式单独非 scoped，避免选择器失效 */
.public-sea-dialog .el-form.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.public-sea-dialog .public-sea-form-actions {
  flex: 1 1 100%;
  width: 100%;
  margin-right: 0;
}

.public-sea-dialog .public-sea-form-actions .el-form-item__content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-left: 0 !important;
}

.public-sea-dialog .public-sea-action-btns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.public-sea-import-popper .import-panel {
  width: 100%;
}

.public-sea-import-popper .import-panel-title {
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}
</style>
